import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import {
  User,
  Book,
  Trophy,
  Coins,
  Target,
  Bell,
  Gift,
  TrendingUp,
  BookOpen,
  BookMarked,
  FlaskConical,
  CalendarDays,
  ClipboardList,
  Zap,
} from "lucide-react";
import UserSettingsModal from "../components/UserSettingsModal";

import "../styles/homePage.css";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";

export default function HomePage() {
  // username from localStorage, rest default
  const [user, setUser] = useState({
    name: localStorage.getItem("username") || "Guest",
    coins: 0,
    level: 0,
    xp: 0,
    progressPct: 0,
    xpToNext: 1000,
    streak: 0,
    rank: "Scholar",
    dailySpins: 0,
    completedToday: false,
  });
  const [bookCount, setBookCount] = useState(null);

  useEffect(() => {
    document.title = "Home — Owllet Reading Quest";
  }, []);

  // fetch coins and xp from backend
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (!storedUserId) return;

    // Fetch coins
    apiFetch(`/api/resources/${storedUserId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch coins");
        return res.json();
      })
      .then((data) => {
        if (typeof data.coins === "number") {
          setUser((prev) => ({ ...prev, coins: data.coins }));
        }
      })
      .catch((err) => console.error("Coins fetch error:", err));

    // Fetch Level + XP
    apiFetch(`/api/resources/xp/${storedUserId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch level");
        return res.json();
      })
      .then((data) => {
        setUser((prev) => ({
          ...prev,
          level:       typeof data.level       === "number" ? data.level       : prev.level,
          xp:          typeof data.xp          === "number" ? data.xp          : prev.xp,
          progressPct: typeof data.progressPct === "number" ? data.progressPct : prev.progressPct,
          xpToNext:    typeof data.xpToNext    === "number" ? data.xpToNext    : prev.xpToNext,
        }));
      })
      .catch((err) => console.error("Level fetch error:", err));

    // Fetch enabled book count from catalogue
    apiFetch(`/api/books/catalogue`)
      .then((res) => res.json())
      .then((data) => {
        const count = Object.keys(data.bookInfo || {}).length;
        setBookCount(count);
      })
      .catch(() => {});

    // Fetch streak from calendar API (also triggers check-in)
    apiFetch(`/api/calendar/checkin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: parseInt(storedUserId, 10) }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (typeof data.streak === "number") {
          setUser((prev) => ({ ...prev, streak: data.streak }));
        }
      })
      .catch(() => {});
  }, []);

  const notifications = ["NONE"];

  const navItems = [
    {
      title: "Vocabulary",
      description: "Vocab matching, synonyms, antonyms and more",
      icon: <TrendingUp size={24} />,
      status: "3 games available",
      color: "nav-teal",
      link: "/games/vocabulary",
    },
    {
      title: "Comprehension",
      description: "Spot the word, MCQ master, ranked and more",
      icon: <BookOpen size={24} />,
      status: "4 games available",
      color: "nav-blue",
      link: "/games/comprehension",
    },
    {
      title: "Books",
      description: "Read chapters and answer book comprehension questions",
      icon: <BookMarked size={24} />,
      status:
        bookCount !== null ? `${bookCount} books available` : "Books available",
      color: "nav-purple",
      link: "/games/books",
    },
    {
      title: "Test Modes",
      description:
        "Vocabulary knowledge tests — definitions, synonyms, antonyms and mixed",
      icon: <FlaskConical size={24} />,
      status: "Vocab tests available",
      color: "nav-pink",
      link: "/test-modes",
    },
    {
      title: "My Results",
      description:
        "Review your last 10 results from Comprehension, MCQ, Ranked and Book games",
      icon: <ClipboardList size={24} />,
      status: "Last 10 per game",
      color: "nav-blue",
      link: "/my-results",
    },
    {
      title: "My Stats",
      description: "See your game statistics",
      icon: <Trophy size={24} />,
      status: "Track your progression",
      color: "nav-orange",
      link: "/statisticsPage",
    },
    {
      title: "Login Calendar",
      description:
        "Track your daily logins, collect bonus coins and build your streak",
      icon: <CalendarDays size={24} />,
      status: `${user.streak} day streak`,
      color: "nav-orange",
      link: "/calendar",
    },
    {
      title: "Owl Hatchery",
      description: "Spin for collectible owls",
      icon: <Gift size={24} />,
      status: `${user.dailySpins} free spins left`,
      color: "nav-yellow",
      urgent: user.dailySpins > 0,
      link: "/owlCollectingGacha",
    },
    {
      title: "My Collection",
      description: "View your owl collection",
      icon: <Target size={24} />,
      status: "?/? Pirate Owls",
      color: "nav-teal",
      link: "/owlNest",
    },
  ];

  return (
    <div className="homepage">
      <header className="header">
        <div className="header-left">
          <div className="logo">
            <Book size={28} color="#fff" aria-hidden="true" />
          </div>
          <div>
            <h1 className="title">Owllet Reading Quest</h1>
            <p className="subtitle">Master comprehension through adventure</p>
          </div>
        </div>
        <div className="header-right">
          <div className="profile">
            <User size={32} color="#6b21a8" aria-hidden="true" />
            <div className="profile-info">
              <p className="profile-name">{user.name}</p>
              <p className="profile-rank">
                Level {user.level} {user.rank}
              </p>
            </div>
            <div
              className="notifications"
              role="status"
              aria-label={
                notifications.length > 0
                  ? `${notifications.length} notifications`
                  : "No notifications"
              }
            >
              <Bell size={24} aria-hidden="true" />
              {notifications.length > 0 && (
                <span className="notification-badge" aria-hidden="true">
                  {notifications.length}
                </span>
              )}
            </div>
            <UserSettingsModal />
          </div>
        </div>
      </header>

      <main className="main-content">
        <dl className="stats-bar">
          <div className="stat-card yellow">
            <Coins size={28} aria-hidden="true" />
            <div>
              <dd className="stat-value">{user.coins}</dd>
              <dt className="stat-label">Coins</dt>
            </div>
          </div>
          <div className="stat-card green">
            <Target size={28} aria-hidden="true" />
            <div>
              <dd className="stat-value">{user.streak}</dd>
              <dt className="stat-label">Day Streak</dt>
            </div>
          </div>
          <div className="stat-card purple">
            <Trophy size={28} aria-hidden="true" />
            <div>
              <dd className="stat-value"> </dd>
              <dt className="stat-label"> </dt>
            </div>
          </div>
          <div className="stat-card blue stat-card--xp">
            <Zap size={24} className="stat-xp-icon" aria-hidden="true" />
            <div className="stat-xp-wrap">
              <div className="stat-xp-header">
                <span className="stat-xp-level">Level {user.level}</span>
                <span className="stat-xp-sub">{user.xp} / {user.xp + user.xpToNext} XP</span>
              </div>
              <div className="stat-xp-track">
                <div className="stat-xp-fill" style={{ width: `${Math.min(100, user.progressPct)}%` }} />
              </div>
            </div>
          </div>
        </dl>

        <div className="nav-grid">
          {navItems.map((item, index) => (
            <div
              key={index}
              className={`nav-card ${item.urgent ? "urgent" : ""} ${item.comingSoon ? "nav-card-coming-soon" : ""}`}
            >
              <div className={`nav-icon ${item.color}`}>{item.icon}</div>
              {item.urgent && <span className="urgent-badge">New</span>}
              {item.comingSoon && (
                <span className="coming-soon-badge">Coming Soon</span>
              )}
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <div className="nav-footer">
                <span>{item.status}</span>
                {!item.comingSoon && (
                  <Link to={item.link}>
                    <button className="enter-btn">Enter →</button>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
